import { useListFilters, useListPagination } from "../../hooks/useListState.js";
import { validateListFilters } from "../../lib/listState.js";
import { downloadPrivateFile } from "../../components/FileDownload.jsx";
import { useEffect, useMemo, useState } from "react";
import { RouteTargetNotice } from "../../components/RouteTargetNotice.jsx";
import { DeleteOutlined, EditOutlined, DownloadOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { App as AntApp, Button, Card, Drawer, Form, Input, Popconfirm, Select, Table, Upload } from "antd";
import { AppEmpty } from "../../components/Feedback.jsx";
import { RotateCcw } from "lucide-react";
import { readLimitedFiles } from "../../utils/files.js";
import { schoolDisplayName } from "../../utils/schoolDisplay.js";

function SchoolNameInput({ value, academyInfo, ...props }) {
  return <Input {...props} value={schoolDisplayName(value, academyInfo)} />;
}

const currentYear = String(new Date().getFullYear());
const semesters = ["1학기", "2학기", "여름특강", "겨울특강"];
const examKinds = ["중간고사", "기말고사", "모의고사", "단원평가"];
const emptyFilter = { year: "", semester: "", examKind: "", school: "", grade: "", subject: "" };
const emptyEntry = { year: currentYear, semester: "1학기", examKind: "중간고사", school: "", grade: "", subject: "", description: "" };

function normalizeExamKind(value) {
  return { "중간": "중간고사", "기말": "기말고사", "모의": "모의고사", "학원": "단원평가" }[value] || value || "";
}
function formatRegisteredDate(value) {
  return value ? new Date(value).toLocaleDateString("ko-KR") : "-";
}

function fileListValue(event) {
  return Array.isArray(event) ? event : event?.fileList;
}
function getArchiveFiles(item) {
  if (item.files?.length) return item.files;
  return item.fileName ? [{ name: item.fileName, type: item.fileType, data: item.fileData }] : [];
}

function asExistingUploadFile(file, index) {
  return { uid: "existing-" + index + "-" + file.name, name: file.name, status: "done", existingFile: file };
}

export function ArchivePage({ store, navigation }) {
  const { state, update, createId } = store;
  const [filter, setFilter] = useListFilters("list:archive:filter", emptyFilter, v => validateListFilters(v, emptyFilter, {
    year: (store.loading || !!store.error) ? null : state.archive.map(i => i.year), semester: semesters, examKind: examKinds,
    school: (store.loading || !!store.error) ? null : state.archive.map(i => i.school),
    grade: (store.loading || !!store.error) ? null : state.archive.map(i => i.grade), subject: (store.loading || !!store.error) ? null : state.archive.map(i => i.subject),
  }));
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [drawerMode, setDrawerMode] = useState("");
  const [form] = Form.useForm();
  const { message, modal } = AntApp.useApp();
  const routeArchiveId = navigation?.route.kind === "archive" ? navigation.route.id : "";
  useEffect(() => {
    if (!routeArchiveId || store.loading) return;
    const item = state.archive.find(item => item.id === routeArchiveId);
    if (item) {
      setEditingId(item.id); setDrawerMode("view"); setEditorOpen(true);
    }
  }, [routeArchiveId, store.loading]);

  const filterOptions = useMemo(() => {
    const values = (key) => [...new Set(state.archive.map((item) => item[key]).filter(Boolean))].map((value) => ({ value, label: value }));
    return { years: values("year"), schools: values("school").map(option => ({ ...option, label: schoolDisplayName(option.value, state.academyInfo) })), grades: values("grade"), subjects: values("subject") };
  }, [state.archive, state.academyInfo]);

  const results = useMemo(() => state.archive.filter((item) => {
    return (!filter.year || item.year === filter.year) &&
      (!filter.semester || item.semester === filter.semester) &&
      (!filter.examKind || normalizeExamKind(item.examKind) === filter.examKind) &&
      (!filter.school || item.school === filter.school) &&
      (!filter.grade || item.grade === filter.grade) &&
      (!filter.subject || item.subject === filter.subject);
  }), [filter, state.archive]);

  const pagination = useListPagination("list:archive:page", results.length, filter, store.loading || !!store.error);

  const activeArchiveItem = state.archive.find((item) => item.id === editingId) || null;

  function populateForm(item) {
    form.setFieldsValue({ ...emptyEntry, ...item, examKind: normalizeExamKind(item.examKind), files: getArchiveFiles(item).map(asExistingUploadFile) });
  }

  function openCreate() {
    setEditingId("");
    setDrawerMode("create");
    form.setFieldsValue({ ...emptyEntry, files: [] });
    setEditorOpen(true);
  }

  function openDetail(item) {
    if (navigation) { navigation.open("archive", item.id); return; }
    setEditingId(item.id);
    setDrawerMode("view");
    populateForm(item);
    setEditorOpen(true);
  }

  function startEdit() {
    if (activeArchiveItem) populateForm(activeArchiveItem);
    setDrawerMode("edit");
  }

  function closeEditor() {
    if (routeArchiveId) { navigation.close(); return; }
    setEditorOpen(false);
    setEditingId("");
    setDrawerMode("");
    form.resetFields();
  }
  async function save(values) {
    const current = state.archive.find((item) => item.id === editingId);
    const selectedFiles = values.files || [];
    const retainedFiles = selectedFiles.filter((file) => file.existingFile).map((file) => file.existingFile);
    const rawFiles = selectedFiles.map((file) => file.originFileObj).filter(Boolean);

    if (!current && !rawFiles.length) return;
    if (retainedFiles.length + rawFiles.length > 5) {
      message.warning("자료당 파일은 최대 5개까지 첨부할 수 있습니다.");
      return;
    }

    try {
      const addedFiles = await readLimitedFiles(rawFiles, { maxCount: 5 - retainedFiles.length, types: ["image/jpeg", "image/png", "application/pdf"] });
      const files = [...retainedFiles, ...addedFiles];
      if (!files.length) {
        message.warning("최소 한 개의 파일을 첨부해 주세요.");
        return;
      }

      const primaryFile = files[0];
      const next = {
        year: values.year.trim(),
        semester: values.semester,
        examKind: values.examKind,
        school: values.school.trim(),
        grade: values.grade.trim(),
        subject: values.subject.trim(),
        description: values.description?.trim() || "",
        files,
        fileName: primaryFile.name,
        fileType: primaryFile.type,
        fileData: primaryFile.data
      };

      const duplicate = state.archive.some((item) => item.id !== editingId && item.year === next.year && item.semester === next.semester && normalizeExamKind(item.examKind) === next.examKind && item.school === next.school && item.grade === next.grade && item.subject === next.subject && getArchiveFiles(item).some((file) => files.some((nextFile) => file.name === nextFile.name)));
      if (duplicate) {
        const shouldContinue = await new Promise((resolve) => {
          modal.confirm({
            title: "같은 분류의 자료가 있습니다",
            content: "동일한 시험 분류와 자료 정보에 같은 이름의 파일이 있습니다. 그래도 저장할까요?",
            okText: "계속 저장",
            cancelText: "취소",
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        if (!shouldContinue) return;
      }

      if (!await update((draft) => {
        if (editingId) {
          Object.assign(draft.archive.find((item) => item.id === editingId), next);
        } else {
          draft.archive.unshift({ id: createId("arc"), ...next, createdAt: new Date().toISOString() });
        }
      })) return;
      closeEditor();
    } catch (error) {
      message.error(error.message || "자료를 저장하지 못했습니다.");
    }
  }

  async function download(item) {
    try { for (const file of getArchiveFiles(item)) await downloadPrivateFile(file); } catch (error) { message.error(error.message); }
  }
  async function remove(id) {
    if (!await update((draft) => {
      draft.archive = draft.archive.filter((item) => item.id !== id);
    })) return;
  }

  const columns = [
    { title: "\uc5f0\ub3c4", dataIndex: "year", width: 74, render: (value) => value || "-" },
    { title: "\ud559\uae30", dataIndex: "semester", width: 88, render: (value) => value || "-" },
    { title: "\uc2dc\ud5d8 \uc885\ub958", dataIndex: "examKind", width: 104, render: (value) => normalizeExamKind(value) || "-" },
    { title: "\ud559\uad50", dataIndex: "school", width: 110, render: (value) => schoolDisplayName(value, state.academyInfo) || "-" },
    { title: "\ud559\ub144", dataIndex: "grade", width: 74, render: (value) => value ? value + "\ud559\ub144" : "-" },
    { title: "\uacfc\ubaa9", dataIndex: "subject", width: 90, render: (value) => value || "-" },
    {
      title: "자료",
      width: 180,
      ellipsis: true,
      render: (_, item) => {
        const fileNames = getArchiveFiles(item).map((file) => file.name).filter(Boolean).join(", ");
        return <span className="archive-file-names" title={fileNames || undefined}>{fileNames || "-"}</span>;
      }
    },
    { title: "\ub4f1\ub85d\uc77c", dataIndex: "createdAt", width: 104, render: formatRegisteredDate },
    {
      title: "\ub2e4\uc6b4\ub85c\ub4dc",
      key: "actions",
      align: "center",
      width: 64,
      render: (_, item) => (
        <div className="archive-row-actions">
          <Button
            type="text"
            size="small"
            aria-label={"\ub2e4\uc6b4\ub85c\ub4dc"}
            icon={<DownloadOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              download(item);
            }}
          />
        </div>
      )
    }
  ];
  if (routeArchiveId && !state.archive.some(item => item.id === routeArchiveId)) return <RouteTargetNotice loading={store.loading} onBack={() => navigation.close()} />;
  return (
    <div className="archive-page page-stack">
      <Card title="기출 자료 검색" className="academy-management-card archive-filter-card">
        <div className="student-filter-form">
          <div className="student-filter-inline archive-simple-toolbar">
            <div className={`student-filter-control${filter.year ? " is-active" : ""}`}><span>년도:</span><Select value={filter.year || undefined} onChange={(year) => setFilter({ ...filter, year: year || "" })} options={filterOptions.years} placeholder="전체" allowClear /></div>
            <div className={`student-filter-control${filter.semester ? " is-active" : ""}`}><span>학기:</span><Select value={filter.semester || undefined} onChange={(semester) => setFilter({ ...filter, semester: semester || "" })} options={semesters.map((value) => ({ value, label: value }))} placeholder="전체" allowClear /></div>
            <div className={`student-filter-control${filter.examKind ? " is-active" : ""}`}><span>시험:</span><Select value={filter.examKind || undefined} onChange={(examKind) => setFilter({ ...filter, examKind: examKind || "" })} options={examKinds.map((value) => ({ value, label: value }))} placeholder="전체" allowClear /></div>
            <div className={`student-filter-control${filter.school ? " is-active" : ""}`}><span>학교:</span><Select value={filter.school || undefined} onChange={(school) => setFilter({ ...filter, school: school || "" })} options={filterOptions.schools} placeholder="전체" allowClear showSearch /></div>
            <div className={`student-filter-control${filter.grade ? " is-active" : ""}`}><span>학년:</span><Select value={filter.grade || undefined} onChange={(grade) => setFilter({ ...filter, grade: grade || "" })} options={filterOptions.grades} placeholder="전체" allowClear /></div>
            <div className={`student-filter-control${filter.subject ? " is-active" : ""}`}><span>과목:</span><Select value={filter.subject || undefined} onChange={(subject) => setFilter({ ...filter, subject: subject || "" })} options={filterOptions.subjects} placeholder="전체" allowClear /></div>
            <Button className="student-filter-reset" icon={<RotateCcw size={14} />} onClick={() => setFilter(emptyFilter)}>필터 초기화</Button>
          </div>
        </div>
      </Card>

      <Card className="academy-management-card archive-results-card">
        <div className="academy-tab-panel archive-list-section">
          <div className="section-toolbar">
            <div className="section-heading-meta">
              <h3>기출 자료 목록</h3>
              <p className="archive-list-description">학교·학년·과목별 기출 자료를 확인하고 관리합니다.</p>
              <span>검색 결과 {results.length}건</span>
            </div>
            <div className="archive-list-actions section-toolbar-actions">
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>자료 업로드</Button>
            </div>
          </div>
          <Table
            className="management-list-table archive-list-table"
            onRow={(item) => ({
              className: "archive-row-clickable",
              onClick: () => openDetail(item),
              onKeyDown: (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openDetail(item); } },
              role: "button",
              tabIndex: 0
            })}
            rowKey="id"
            columns={columns}
            dataSource={results}
            pagination={pagination}
            scroll={{ x: 980 }}
            locale={{ emptyText: <AppEmpty description="조건에 맞는 기출 자료가 없습니다." /> }}
          />
        </div>
      </Card>

      <Drawer
        className="archive-upload-drawer"
        title={drawerMode === "create" ? "\uae30\ucd9c \uc790\ub8cc \uc5c5\ub85c\ub4dc" : drawerMode === "edit" ? "\uae30\ucd9c \uc790\ub8cc \uc218\uc815" : "\uae30\ucd9c \uc790\ub8cc \uc0c1\uc138"}
        open={editorOpen}
        onClose={closeEditor}
        closable={{ placement: "end" }}
        destroyOnHidden
        width={560}
        footer={
          <div className="overlay-footer-actions">
            {drawerMode === "view" ? (
              <>
                <Button type="primary" icon={<EditOutlined />} onClick={startEdit}>{"\uc218\uc815"}</Button>
                <Popconfirm title={"\uc774 \uc790\ub8cc\ub97c \uc0ad\uc81c\ud560\uae4c\uc694?"} okText={"\uc0ad\uc81c"} cancelText={"\ucde8\uc18c"} okButtonProps={{ danger: true }} onConfirm={async () => { await remove(editingId); }}>
                  <Button disabled title="현재 서버는 기출 삭제 API를 제공하지 않습니다." danger icon={<DeleteOutlined />}>{"\uc0ad\uc81c"}</Button>
                </Popconfirm>
              </>
            ) : (
              <>
                <Button onClick={closeEditor}>{"\ucde8\uc18c"}</Button>
                <Button type="primary" onClick={() => form.submit()}>{"\uc800\uc7a5"}</Button>
              </>
            )}
          </div>
        }
      >
        {drawerMode === "view" && activeArchiveItem ? (
          <div className="archive-detail-view">
            <section className="archive-detail-section">
              <h3>시험 분류</h3>
              <dl className="archive-detail-grid three">
                <div><dt>년도</dt><dd>{activeArchiveItem.year || "-"}</dd></div>
                <div><dt>학기/시즌</dt><dd>{activeArchiveItem.semester || "-"}</dd></div>
                <div><dt>시험 종류</dt><dd>{normalizeExamKind(activeArchiveItem.examKind) || "-"}</dd></div>
              </dl>
            </section>

            <section className="archive-detail-section">
              <h3>자료 정보</h3>
              <dl className="archive-detail-grid">
                <div><dt>학교</dt><dd>{schoolDisplayName(activeArchiveItem.school, state.academyInfo) || "-"}</dd></div>
                <div><dt>학년</dt><dd>{activeArchiveItem.grade || "-"}</dd></div>
                <div><dt>과목</dt><dd>{activeArchiveItem.subject || "-"}</dd></div>
                <div><dt>등록일</dt><dd>{formatRegisteredDate(activeArchiveItem.createdAt)}</dd></div>
                <div className="archive-detail-wide"><dt>설명</dt><dd>{activeArchiveItem.description || "등록된 설명이 없습니다."}</dd></div>
              </dl>
            </section>

            <section className="archive-detail-section">
              <div className="archive-detail-section-heading">
                <h3>첨부 파일</h3>
                <Button size="small" icon={<DownloadOutlined />} onClick={() => download(activeArchiveItem)}>전체 다운로드</Button>
              </div>
              <div className="archive-detail-files">
                {getArchiveFiles(activeArchiveItem).map((file, index) => (
                  <div className="archive-detail-file" key={file.name + index}>
                    <div><span>{file.name || "이름 없는 파일"}</span><small>{file.type || "파일"}</small></div>
                    <Button type="text" size="small" icon={<DownloadOutlined />} aria-label={file.name + " 다운로드"} onClick={() => download({ files: [file] })} />
                  </div>
                ))}
              </div>
            </section>
          </div>
        ) : (
          <Form form={form} layout="vertical" onFinish={save} initialValues={emptyEntry}>
            <section className="archive-form-section">
              <h3>시험 분류</h3>
              <div className="archive-form-grid three">
                <Form.Item name="year" label="년도" rules={[{ required: true, whitespace: true, message: "년도를 입력해 주세요." }]}><Input placeholder="2026" /></Form.Item>
                <Form.Item name="semester" label="학기/시즌" rules={[{ required: true }]}><Select options={semesters.map((value) => ({ value, label: value }))} /></Form.Item>
                <Form.Item name="examKind" label="시험 종류" rules={[{ required: true }]}><Select options={examKinds.map((value) => ({ value, label: value }))} /></Form.Item>
              </div>
            </section>

            <section className="archive-form-section">
              <h3>자료 정보</h3>
              <div className="archive-form-grid">
                <Form.Item name="school" label="학교" rules={[{ required: true, whitespace: true, message: "학교를 입력해 주세요." }]}><SchoolNameInput academyInfo={state.academyInfo} placeholder="예: 새로고등학교" /></Form.Item>
                <Form.Item name="grade" label="학년" rules={[{ required: true, whitespace: true, message: "학년을 입력해 주세요." }]}><Input placeholder="예: 2학년" /></Form.Item>
                <Form.Item name="subject" label="과목" rules={[{ required: true, whitespace: true, message: "과목을 입력해 주세요." }]}><Input placeholder="예: 수학" /></Form.Item>
                <Form.Item name="description" label="설명 (선택)" className="archive-wide-field"><Input.TextArea disabled rows={3} placeholder="자료에 대한 추가 설명을 입력하세요." /></Form.Item>
              </div>
              <Form.Item name="files" label="첨부 파일 (PDF, JPG, PNG)" valuePropName="fileList" getValueFromEvent={fileListValue} rules={[{ required: true, message: "파일을 한 개 이상 첨부해 주세요." }]} extra={drawerMode === "edit" ? "기존 파일은 유지되며, 새 파일을 추가하거나 목록에서 제거할 수 있습니다." : "자료당 최대 5개까지 첨부할 수 있습니다."}>
                <Upload.Dragger onRemove={(file) => !file.existingFile} beforeUpload={() => false} maxCount={5} accept=".pdf,.jpg,.jpeg,.png" showUploadList={{ showRemoveIcon: true }}>
                  <p className="ant-upload-drag-icon"><UploadOutlined /></p>
                  <p className="ant-upload-text">클릭하여 파일 선택 또는 여기를 드래그</p>
                  <p className="ant-upload-hint">PDF, JPG, PNG 파일을 첨부할 수 있습니다.</p>
                </Upload.Dragger>
              </Form.Item>
            </section>
          </Form>
        )}
      </Drawer>

    </div>
  );
}
